'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';

import SwiperButtonPrev from 'app/components/common/SwiperButtonPrev';
import SwiperButtonNext from 'app/components/common/SwiperButtonNext';

import 'swiper/css';
import { sliderButtonThemes } from 'app/utility/themes';
import { useRef } from 'react';
import classNames from 'classnames';
import { ICalendarSlot } from '@/app/interfaces/schedule';
import { useMediaQuery } from 'usehooks-ts';

interface IScheduleSliderProps {
  items: ICalendarSlot[];
  initialIndex?: number;
  onChange: (
    activeRangeIndexStart: number,
    calendarActiveRangeLength: number
  ) => void;
}

export default function ScheduleSlider({
  items,
  onChange,
  initialIndex = 0
}: IScheduleSliderProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const isScreen_lg = useMediaQuery('(min-width: 1024px)');

  return (
    <div
      className="p-1 md:p-1.5 border border-brown rounded bg-gray"
      key={`${isScreen_lg}`}
    >
      <Swiper
        slidesPerView={3}
        slidesPerGroup={1}
        spaceBetween={-4}
        centeredSlides={true}
        initialSlide={initialIndex}
        breakpoints={{
          768: {
            spaceBetween: -12,
            slidesPerView: 3,
            slidesPerGroup: 1,
            initialSlide: initialIndex
          },
          1024: {
            slidesPerView: 7,
            slidesPerGroup: 7,
            centeredSlides: false,
            initialSlide: 0
          }
        }}
        onSwiper={swiper => {
          swiper.on('slideChange', () => {
            onChange?.(swiper.activeIndex, swiper.params.slidesPerGroup || 1);
          });
          swiper.on('update', () => {
            onChange?.(swiper.activeIndex, swiper.params.slidesPerGroup || 1);
          });
          swiperRef.current = swiper;
          onChange?.(swiper.activeIndex, swiper.params.slidesPerGroup || 1);
        }}
        className="!px-9 md:!px-12"
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={index}
            className="group px-1 md:px-3"
            style={{ height: 'auto' }}
          >
            <div
              className={classNames(
                'text-p3 flex justify-center items-center gap-1 p-2 xl:p-4 rounded min-h-full group-[&.swiper-slide-active]:bg-white group-[&.swiper-slide-active]:lg:bg-transparent'
              )}
            >
              {item.isToday && <span>Сегодня</span>}
              {!item.isToday && (
                <div className="flex flex-col items-center gap-1">
                  <span>
                    {item.dayOfMonth}{' '}
                    <span className="capitalize">{item.month}</span>
                  </span>
                  <span className="capitalize">{item.dayOfWeek}</span>
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
        <div
          className="absolute z-10 top-0 left-0 h-full"
          key={`prev-${items.length}`}
        >
          <SwiperButtonPrev
            className="w-9 md:w-12 h-full rounded-md"
            iconClassName="w-4 h-4"
            theme={sliderButtonThemes.gray}
          />
        </div>
        <div
          className="absolute z-10 top-0 right-0 h-full"
          key={`next-${items.length}`}
        >
          <SwiperButtonNext
            className="w-9 md:w-12 h-full rounded-md"
            iconClassName="w-4 h-4"
            theme={sliderButtonThemes.gray}
          />
        </div>
      </Swiper>
    </div>
  );
}
